import Vue from 'vue'

export default {
  setLoading(state, payload) {
    state.loading = payload
  },
  setSurvey(state, payload) {
    state.survey = payload
  },
  setConfirmationAnswers(state, payload) {
    state.confirmationAnswers = payload
  },
  setConfirmationAnswer(state, payload) {
    Vue.set(state.confirmationAnswers, payload.answerId, payload.content)
  },
  /**
   *
   * @param {*} state
   * @param {{ sectionId: string, questionId: string, parentQuestionId: string | null, parentAnswerId: string | null, rootParentQuestionId: string | null, rootParentAnswerId: string | null, updatedAnswers: [] }} payload
   * @returns
   */
  setUpdatedAnswers(state, payload) {
    const {
      sectionId,
      questionId,
      parentAnswerId,
      parentQuestionId,
      rootParentAnswerId,
      rootParentQuestionId,
      updatedAnswers,
    } = payload

    // Stop if there is no survey
    if (!state.survey) return

    const sectionIndex = state.survey.sections
      ? state.survey.sections.findIndex(el => el.id === sectionId)
      : -1

    // Stop if there is no section with this sectionId
    if (sectionIndex === -1) return

    const rootParentQuestionIndex = state.survey.sections?.[sectionIndex]
      ?.questions
      ? state.survey.sections[sectionIndex].questions.findIndex(
          el =>
            el.id === rootParentQuestionId ||
            el.id === parentQuestionId ||
            el.id === questionId,
        )
      : -1

    // Stop if there is no question in this section and there is provided root parent question id
    if (rootParentQuestionIndex === -1 && !!rootParentQuestionId) return

    const rootParentAnswerIndex =
      rootParentQuestionIndex !== -1
        ? state.survey.sections[sectionIndex].questions?.[
            rootParentQuestionIndex
          ]?.answers
          ? state.survey.sections[sectionIndex].questions[
              rootParentQuestionIndex
            ].answers.findIndex(
              el => el.id === rootParentAnswerId || el.id === parentAnswerId,
            )
          : -1
        : -1

    // Stop if there is no answer in this section and there is provided root parent answer id
    if (rootParentAnswerIndex === -1 && !!rootParentAnswerId) return

    // New array of answers
    let newAnswers = []
    if (
      rootParentAnswerId &&
      rootParentQuestionId &&
      parentQuestionId &&
      parentAnswerId
    ) {
      // For double embedded question
      const firstLevelEmbeddedQuestion =
        state.survey.sections[sectionIndex]?.questions?.[
          rootParentQuestionIndex
        ]?.answers?.[rootParentAnswerIndex]?.embeddedQuestion || null

      // If there is embedded question or parentQuestionId is not valid
      if (
        firstLevelEmbeddedQuestion === null ||
        firstLevelEmbeddedQuestion.id !== parentQuestionId
      )
        return

      const parentAnswerIndex = firstLevelEmbeddedQuestion?.answers
        ? firstLevelEmbeddedQuestion.answers.findIndex(
            el => el.id === parentAnswerId,
          )
        : -1

      // If cannot find answer in first level embedded question
      if (parentAnswerIndex === -1 && parentAnswerId) return

      const secondLevelEmbeddedQuestion =
        firstLevelEmbeddedQuestion?.answers?.[parentAnswerIndex]
          ?.embeddedQuestion || null

      // If there is embedded question or parentQuestionId is not valid
      if (
        secondLevelEmbeddedQuestion === null ||
        secondLevelEmbeddedQuestion.id !== questionId
      )
        return

      // Assign current state of answers
      newAnswers = [...secondLevelEmbeddedQuestion.answers]

      // Change answers
      for (const [_, newAnswer] of updatedAnswers.entries()) {
        const answerIndex =
          newAnswers.findIndex(el => el.id === newAnswer.id) ?? -1

        // Stop if there is no answer in this embeded question
        if (answerIndex === -1) return

        // Assign new value of answer
        newAnswers[answerIndex] = { ...newAnswer }
      }

      // Change survey data with updated answers
      if (
        state.survey.sections[sectionIndex].questions?.[rootParentQuestionIndex]
          ?.answers?.[rootParentAnswerIndex]?.embeddedQuestion?.answers?.[
          parentAnswerIndex
        ]?.embeddedQuestion
      ) {
        Vue.set(
          state.survey.sections[sectionIndex].questions[rootParentQuestionIndex]
            .answers[rootParentAnswerIndex].embeddedQuestion.answers[
            parentAnswerIndex
          ].embeddedQuestion,
          'answers',
          [...newAnswers],
        )
      }
    } else if (parentAnswerId && parentQuestionId) {
      // For embedded question
      const answersInEmbeddedQuestion =
        state.survey.sections[sectionIndex]?.questions?.[
          rootParentQuestionIndex
        ]?.answers?.[rootParentAnswerIndex]?.embeddedQuestion?.answers || null

      // If there is no answers
      if (answersInEmbeddedQuestion === null) return

      // Assign current state of answers
      newAnswers = [...answersInEmbeddedQuestion]

      // Change answers
      for (const [_, newAnswer] of updatedAnswers.entries()) {
        const answerIndex =
          newAnswers.findIndex(el => el.id === newAnswer.id) ?? -1

        // Stop if there is no answer in this embeded question
        if (answerIndex === -1) return

        // Assign new value of answer
        newAnswers[answerIndex] = { ...newAnswer }
      }

      // Change survey data with updated answers
      if (
        state.survey.sections[sectionIndex].questions[rootParentQuestionIndex]
          .answers[rootParentAnswerIndex].embeddedQuestion
      ) {
        Vue.set(
          state.survey.sections[sectionIndex].questions[rootParentQuestionIndex]
            .answers[rootParentAnswerIndex].embeddedQuestion,
          'answers',
          [...newAnswers],
        )
      }
    } else {
      // For non embedded question
      const answersInNonEmbeddedQuestion =
        state.survey.sections[sectionIndex]?.questions?.[
          rootParentQuestionIndex
        ]?.answers || null

      // If there is no answers
      if (answersInNonEmbeddedQuestion === null) return

      // Assign current state of answers
      newAnswers = [...answersInNonEmbeddedQuestion]

      // Change answers
      for (const [_, newAnswer] of updatedAnswers.entries()) {
        const answerIndex =
          newAnswers.findIndex(el => el.id === newAnswer.id) ?? -1

        // Stop if there is no answer in this non embeded question
        if (answerIndex === -1) return

        // Assign new value of answer
        newAnswers[answerIndex] = { ...newAnswer }
      }

      // Change survey data with updated answer
      if (
        state.survey.sections[sectionIndex].questions[rootParentQuestionIndex]
      ) {
        Vue.set(
          state.survey.sections[sectionIndex].questions[
            rootParentQuestionIndex
          ],
          'answers',
          [...newAnswers],
        )
      }
    }
  },
  /**
   *
   * @param {*} state
   * @param {{ questionId: string, parentQuestionId: string | null, parentAnswerId: string | null, rootParentQuestionId: string | null, rootParentAnswerId: string | null, updatedAnswers: [] }} payload
   * @returns
   */
  setUpdatedFeedbackAnswers(state, payload) {
    const {
      questionId,
      parentAnswerId,
      parentQuestionId,
      rootParentAnswerId,
      rootParentQuestionId,
      updatedAnswers,
    } = payload

    // Stop if there is no survey
    if (!state.survey) return

    const rootParentQuestionIndex = state.survey.feedback
      ? state.survey.feedback.findIndex(
          el =>
            el.id === rootParentQuestionId ||
            el.id === parentQuestionId ||
            el.id === questionId,
        )
      : -1

    // Stop if there is no question in this section and there is provided root parent question id
    if (rootParentQuestionIndex === -1 && !!rootParentQuestionId) return

    const rootParentAnswerIndex =
      rootParentQuestionIndex !== -1
        ? state.survey.feedback?.[rootParentQuestionIndex]?.answers
          ? state.survey.feedback[rootParentQuestionIndex].answers.findIndex(
              el => el.id === rootParentAnswerId || el.id === parentAnswerId,
            )
          : -1
        : -1

    // Stop if there is no answer in this section and there is provided root parent answer id
    if (rootParentAnswerIndex === -1 && !!rootParentAnswerId) return

    // New array of answers
    let newAnswers = []
    if (
      rootParentAnswerId &&
      rootParentQuestionId &&
      parentQuestionId &&
      parentAnswerId
    ) {
      // For double embedded question
      const firstLevelEmbeddedQuestion =
        state.survey.feedback?.questions?.[rootParentQuestionIndex]?.answers?.[
          rootParentAnswerIndex
        ]?.embeddedQuestion || null

      // If there is embedded question or parentQuestionId is not valid
      if (
        firstLevelEmbeddedQuestion === null ||
        firstLevelEmbeddedQuestion.id !== parentQuestionId
      )
        return

      const parentAnswerIndex = firstLevelEmbeddedQuestion?.answers
        ? firstLevelEmbeddedQuestion.answers.findIndex(
            el => el.id === parentAnswerId,
          )
        : -1

      // If cannot find answer in first level embedded question
      if (parentAnswerIndex === -1 && parentAnswerId) return

      const secondLevelEmbeddedQuestion =
        firstLevelEmbeddedQuestion?.answers?.[parentAnswerIndex]
          ?.embeddedQuestion || null

      // If there is embedded question or parentQuestionId is not valid
      if (
        secondLevelEmbeddedQuestion === null ||
        secondLevelEmbeddedQuestion.id !== questionId
      )
        return

      // Assign current state of answers
      newAnswers = [...secondLevelEmbeddedQuestion.answers]

      // Change answers
      for (const [_, newAnswer] of updatedAnswers.entries()) {
        const answerIndex =
          newAnswers.findIndex(el => el.id === newAnswer.id) ?? -1

        // Stop if there is no answer in this embeded question
        if (answerIndex === -1) return

        // Assign new value of answer
        newAnswers[answerIndex] = { ...newAnswer }
      }

      // Change survey data with updated answers
      if (
        state.survey.feedback?.[rootParentQuestionIndex]?.answers?.[
          rootParentAnswerIndex
        ]?.embeddedQuestion?.answers?.[parentAnswerIndex]?.embeddedQuestion
      ) {
        Vue.set(
          state.survey.feedback[rootParentQuestionIndex].answers[
            rootParentAnswerIndex
          ].embeddedQuestion.answers[parentAnswerIndex].embeddedQuestion,
          'answers',
          [...newAnswers],
        )
      }
    } else if (parentAnswerId && parentQuestionId) {
      // For embedded question
      const answersInEmbeddedQuestion =
        state.survey.feedback?.[rootParentQuestionIndex]?.answers?.[
          rootParentAnswerIndex
        ]?.embeddedQuestion?.answers || null

      // If there is no answers
      if (answersInEmbeddedQuestion === null) return

      // Assign current state of answers
      newAnswers = [...answersInEmbeddedQuestion]

      // Change answers
      for (const [_, newAnswer] of updatedAnswers.entries()) {
        const answerIndex =
          newAnswers.findIndex(el => el.id === newAnswer.id) ?? -1

        // Stop if there is no answer in this embeded question
        if (answerIndex === -1) return

        // Assign new value of answer
        newAnswers[answerIndex] = { ...newAnswer }
      }

      // Change survey data with updated answers
      if (
        state.survey.feedback[rootParentQuestionIndex].answers[
          rootParentAnswerIndex
        ].embeddedQuestion
      ) {
        Vue.set(
          state.survey.feedback[rootParentQuestionIndex].answers[
            rootParentAnswerIndex
          ].embeddedQuestion,
          'answers',
          [...newAnswers],
        )
      }
    } else {
      // For non embedded question
      const answersInNonEmbeddedQuestion =
        state.survey.feedback?.[rootParentQuestionIndex]?.answers || null

      // If there is no answers
      if (answersInNonEmbeddedQuestion === null) return

      // Assign current state of answers
      newAnswers = [...answersInNonEmbeddedQuestion]

      // Change answers
      for (const [_, newAnswer] of updatedAnswers.entries()) {
        const answerIndex =
          newAnswers.findIndex(el => el.id === newAnswer.id) ?? -1

        // Stop if there is no answer in this non embeded question
        if (answerIndex === -1) return

        // Assign new value of answer
        newAnswers[answerIndex] = { ...newAnswer }
      }

      // Change survey data with updated answer
      if (state.survey.feedback[rootParentQuestionIndex]) {
        Vue.set(state.survey.feedback[rootParentQuestionIndex], 'answers', [
          ...newAnswers,
        ])
      }
    }
  },
  /**
   *
   * @param {*} state
   * @param {{ sectionId: string, completed?: boolean }} payload
   * @returns
   */
  setSectionComplete(state, payload) {
    const { sectionId, completed = false } = payload

    // Stop if there is no survey
    if (!state.survey) return
    const sectionIndex = state.survey.sections
      ? state.survey.sections.findIndex(el => el.id === sectionId) ?? -1
      : -1
    // Stop if there is no section with this sectionId
    if (sectionIndex === -1) return

    const newSurveyData = {
      ...state.survey,
    }

    // Change survey data with updated section data
    if (newSurveyData.sections[sectionIndex]) {
      newSurveyData.sections[sectionIndex] = {
        ...newSurveyData.sections[sectionIndex],
        completed,
      }
      state.survey = { ...newSurveyData }
    }
  },
  setSurveyResults(state, payload) {
    state.surveyResults = payload
  },
  setSurveyAdditionalScores(state, payload) {
    state.surveyAdditionalScores = payload
  },
}
